import React, { useEffect, useState } from "react";
import { Seo } from "@/components/Seo";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VOLUNTEER } from "@/constants/testIds";
import { BackLink } from "@/components/BackLink";
import { ReportButton } from "@/components/ReportButton";
import { Send } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(sp.get("to") || null);
  const [activeUser, setActiveUser] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");

  const loadThreads = () => api.threads().then(setThreads).catch(() => {});

  useEffect(() => { loadThreads(); }, []);

  useEffect(() => {
    if (!active) return;
    api.thread(active, { markRead: true }).then(setMsgs).catch(() => setMsgs([]));
    api.publicUser(active).then(setActiveUser).catch(() => setActiveUser(null));
    // Best-effort mark-read; refresh the thread list so unread badges clear.
    api.markThreadRead?.(active).then(() => loadThreads()).catch(() => {});
  }, [active]);

  const send = async () => {
    if (!text.trim() || !active) return;
    await api.sendMessage(active, text.trim());
    setText("");
    const m = await api.thread(active);
    setMsgs(m);
    loadThreads();
  };

  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Seo title="Messages" />
      <BackLink />
      <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Inbox</div>
      <h1 className="font-display text-4xl text-forest mt-2 tracking-tight">Messages</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <aside className="md:col-span-1 border-r border-neutral-200 max-h-[70vh] overflow-y-auto">
          {threads.length === 0 && <div className="p-5 text-sm text-neutral-500">No conversations yet. Open an opportunity and message the organiser.</div>}
          {threads.map(t => (
            <button
              key={t.user_id}
              data-testid={VOLUNTEER.msgThread(t.user_id)}
              onClick={() => { setActive(t.user_id); setSp({ to: t.user_id }); }}
              className={`w-full text-left p-4 border-b border-neutral-100 hover:bg-neutral-50 ${active === t.user_id ? "bg-forest/5" : ""}`}
            >
              <div className="font-medium text-forest">{t.name}</div>
              <div className="text-xs text-neutral-500 truncate mt-0.5">{t.last}</div>
              {t.unread > 0 && <span className="inline-block mt-1 text-[10px] bg-terracotta text-white rounded-full px-1.5">{t.unread}</span>}
            </button>
          ))}
        </aside>
        <section className="md:col-span-2 flex flex-col h-[70vh]">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-neutral-400 text-sm">Select a conversation</div>
          ) : (
            <>
              <div className="p-4 border-b border-neutral-200">
                <div className="font-medium text-forest">{activeUser?.name || "Conversation"}</div>
                {activeUser?.org_name && <div className="text-xs text-neutral-500">{activeUser.org_name}</div>}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {msgs.map(m => {
                  const mine = m.from_user_id === user.user_id;
                  const ts = new Date(m.created_at);
                  const now = new Date();
                  const sameDay = ts.toDateString() === now.toDateString();
                  const label = sameDay
                    ? ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                    : ts.toLocaleDateString([], { day: "2-digit", month: "short" }) + " · " + ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                  const status = m.read ? "read" : "delivered";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] px-4 py-2 rounded-2xl ${mine ? "bg-forest text-white" : "bg-neutral-100 text-neutral-800"}`}>
                        <div className="text-sm whitespace-pre-line">{m.content}</div>
                        <div className={`text-[10px] mt-1 flex items-center gap-1 ${mine ? "text-white/70 justify-end" : "text-neutral-500"}`}>
                          <span data-testid={`msg-timestamp-${m.id}`}>{label}</span>
                          {mine && (
                            <span data-testid={`msg-status-${m.id}`} data-status={status}
                              className={m.read ? "text-sky-300" : "text-white/70"}
                              title={m.read ? `Read${m.read_at ? " · " + new Date(m.read_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}` : "Delivered"}>
                              {m.read ? "✓✓" : "✓"}
                            </span>
                          )}
                          {!mine && (
                            <span className="ml-1 opacity-60 hover:opacity-100 transition-opacity">
                              <ReportButton targetType="message" targetId={m.id} label="" />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-neutral-200 p-3 flex gap-2">
                <Input data-testid={VOLUNTEER.msgInput} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Write a message…" className="h-11" />
                <Button data-testid={VOLUNTEER.msgSend} onClick={send} className="rounded-full bg-forest hover:bg-forest-hover px-5"><Send className="w-4 h-4" /></Button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
